import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { ThemeConfig, LoadingState, Toast, Modal } from '@/types/common'

// Global error state for displaying errors on current page instead of redirecting
export interface GlobalError {
  id: string
  title: string
  message: string
  type: 'error' | 'warning' | 'info'
  dismissible: boolean
  timestamp: Date
  context?: string // Component or view that triggered the error
}

export const useAppStore = defineStore('app', () => {
  // State
  const loading = ref<LoadingState>({
    isLoading: false,
    operation: undefined,
    progress: undefined
  })

  // Global error state - errors shown on current page, not redirected
  const globalErrors = ref<GlobalError[]>([])
  
  const theme = ref<ThemeConfig>({
    mode: 'light',
    primaryColor: '#3b82f6',
    secondaryColor: '#64748b',
    fontFamily: 'Inter',
    fontSize: 'medium'
  })
  
  const sidebar = ref({
    isOpen: true,
    isCollapsed: false,
    isPinned: true
  })
  
  const notifications = ref<Toast[]>([])
  const modals = ref<Modal[]>([])
  
  const appInfo = ref({
    name: 'Zaveato',
    version: '1.0.0',
    description: 'Generic Appointment Management System',
    buildDate: new Date().toISOString(),
    environment: import.meta.env.MODE || 'development',
    // Region-parity note: the *runtime* region is resolved from the shop
    // (`useShopStore().selectShopRegionCode`), the authenticated user's
    // preferred region, or the current host — NOT from this default.
    // The values below are LAST-RESORT fallbacks only, applied when
    // nothing else has surfaced a region yet (very early boot, or a
    // logged-out visitor on a non-canonical host). They are GB because
    // the historical dataset is UK; do not read this as "the app assumes UK".
    defaultRegion: 'GB',
    defaultLocale: 'en-GB', // LAST-RESORT locale fallback (see note above).
    defaultCurrency: 'GBP', // LAST-RESORT currency fallback (see note above).
    defaultTimezone: 'Europe/London', // LAST-RESORT timezone fallback (see note above).
    // ISO 3166-1 alpha-2 codes — mirrors backend
    // `Zaveato.Domain.Common.RegionDefaults` keys and `config/hosts.ts`
    // `RegionCode`. Adding a new region means adding it here + in the
    // four `regionX.ts` maps under `src/config/` + `src/utils/firstDayOfWeek.ts`.
    supportedRegions: ['GB', 'PK', 'AE', 'SA'],
    // Locale + currency lists below are legacy admin-dropdown seeds; entity
    // display MUST use the entity's own currency snapshot (see
    // `formatMoney(amount, entity.currency)`).
    supportedLocales: ['en-GB', 'en-US', 'ur-PK', 'ar-AE', 'ar-SA'],
    supportedCurrencies: ['GBP', 'PKR', 'AED', 'SAR']
  })

  // Getters
  const isDarkMode = computed(() => {
    if (theme.value.mode === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return theme.value.mode === 'dark'
  })
  
  const isProduction = computed(() => appInfo.value.environment === 'production')
  const isDevelopment = computed(() => appInfo.value.environment === 'development')
  
  const activeModal = computed(() => modals.value[modals.value.length - 1])
  const hasActiveModal = computed(() => modals.value.length > 0)

  // Global error getters
  const hasGlobalErrors = computed(() => globalErrors.value.length > 0)
  const latestGlobalError = computed(() => globalErrors.value[globalErrors.value.length - 1])

  // Actions
  function setLoading(isLoading: boolean, operation?: string, progress?: number) {
    loading.value = {
      isLoading,
      operation,
      progress
    }
  }
  
  function startLoading(operation?: string) {
    setLoading(true, operation)
  }
  
  function stopLoading() {
    setLoading(false)
  }
  
  function updateLoadingProgress(progress: number) {
    loading.value.progress = progress
  }

  // Theme functions
  function setTheme(newTheme: Partial<ThemeConfig>) {
    theme.value = { ...theme.value, ...newTheme }
    applyTheme()
    saveTheme()
  }
  
  function toggleDarkMode() {
    const newMode = theme.value.mode === 'dark' ? 'light' : 'dark'
    setTheme({ mode: newMode })
  }
  
  function applyTheme() {
    const root = document.documentElement
    
    if (isDarkMode.value) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
    
    // Apply CSS custom properties
    root.style.setProperty('--primary-color', theme.value.primaryColor)
    root.style.setProperty('--secondary-color', theme.value.secondaryColor)
    root.style.setProperty('--font-family', theme.value.fontFamily)
    
    // Apply font size
    switch (theme.value.fontSize) {
      case 'small':
        root.style.setProperty('--font-size-base', '14px')
        break
      case 'large':
        root.style.setProperty('--font-size-base', '18px')
        break
      default:
        root.style.setProperty('--font-size-base', '16px')
    }
  }
  
  function saveTheme() {
    localStorage.setItem('theme', JSON.stringify(theme.value))
  }
  
  function loadTheme() {
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme) {
      try {
        theme.value = { ...theme.value, ...JSON.parse(savedTheme) }
      } catch (error) {
        console.error('Failed to load theme:', error)
      }
    }
    applyTheme()
  }

  // Sidebar functions
  function toggleSidebar() {
    sidebar.value.isOpen = !sidebar.value.isOpen
    saveSidebarState()
  }
  
  function collapseSidebar() {
    sidebar.value.isCollapsed = !sidebar.value.isCollapsed
    saveSidebarState()
  }
  
  function pinSidebar() {
    sidebar.value.isPinned = !sidebar.value.isPinned
    saveSidebarState()
  }
  
  function saveSidebarState() {
    localStorage.setItem('sidebar', JSON.stringify(sidebar.value))
  }
  
  function loadSidebarState() {
    const savedSidebar = localStorage.getItem('sidebar')
    if (savedSidebar) {
      try {
        sidebar.value = { ...sidebar.value, ...JSON.parse(savedSidebar) }
      } catch (error) {
        console.error('Failed to load sidebar state:', error)
      }
    }
  }

  // Notification functions
  function addNotification(notification: Omit<Toast, 'id'>) {
    const id = Date.now().toString()
    const newNotification: Toast = { ...notification, id }
    
    notifications.value.push(newNotification)
    
    // Auto-remove non-persistent notifications
    if (!notification.persistent) {
      const duration = notification.duration || 5000
      setTimeout(() => {
        removeNotification(id)
      }, duration)
    }
    
    return id
  }
  
  function removeNotification(id: string) {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
    }
  }
  
  function clearNotifications() {
    notifications.value = []
  }

  // Modal functions
  function openModal(modal: Omit<Modal, 'id'>) {
    const id = Date.now().toString()
    const newModal: Modal = { ...modal, id }
    
    modals.value.push(newModal)
    document.body.style.overflow = 'hidden' // Prevent background scrolling
    
    return id
  }
  
  function closeModal(id?: string) {
    if (id) {
      const index = modals.value.findIndex(m => m.id === id)
      if (index > -1) {
        modals.value.splice(index, 1)
      }
    } else {
      modals.value.pop() // Close the topmost modal
    }
    
    if (modals.value.length === 0) {
      document.body.style.overflow = '' // Restore background scrolling
    }
  }
  
  function closeAllModals() {
    modals.value = []
    document.body.style.overflow = ''
  }

  // Global Error functions - for displaying errors on current page instead of redirecting
  function addGlobalError(error: Omit<GlobalError, 'id' | 'timestamp'>): string {
    const id = `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const newError: GlobalError = {
      ...error,
      id,
      timestamp: new Date()
    }

    globalErrors.value.push(newError)

    // Auto-dismiss after 30 seconds if dismissible
    if (error.dismissible) {
      setTimeout(() => {
        dismissGlobalError(id)
      }, 30000)
    }

    return id
  }

  function dismissGlobalError(id: string) {
    const index = globalErrors.value.findIndex(e => e.id === id)
    if (index > -1) {
      globalErrors.value.splice(index, 1)
    }
  }

  function clearGlobalErrors() {
    globalErrors.value = []
  }

  // Show a global error (convenience method)
  function showGlobalError(message: string, title = 'Something went wrong', context?: string) {
    return addGlobalError({
      title,
      message,
      type: 'error',
      dismissible: true,
      context
    })
  }

  // Utility functions
  function getDeviceInfo() {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      screen: {
        width: screen.width,
        height: screen.height,
        colorDepth: screen.colorDepth
      }
    }
  }
  
  function copyToClipboard(text: string): Promise<boolean> {
    return navigator.clipboard.writeText(text)
      .then(() => true)
      .catch(() => false)
  }
  
  function downloadFile(data: any, filename: string, type = 'application/json') {
    const blob = new Blob([data], { type })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    window.URL.revokeObjectURL(url)
  }

  // Initialize app
  function initializeApp() {
    loadTheme()
    loadSidebarState()
    
    // Listen for system theme changes
    if (theme.value.mode === 'system') {
      window.matchMedia('(prefers-color-scheme: dark)')
        .addEventListener('change', applyTheme)
    }
    
    // Handle online/offline status
    window.addEventListener('online', () => {
      addNotification({
        type: 'success',
        message: 'Connection restored',
        duration: 3000
      })
    })
    
    window.addEventListener('offline', () => {
      addNotification({
        type: 'warning',
        message: 'You are offline',
        persistent: true
      })
    })
  }

  return {
    // State
    loading,
    theme,
    sidebar,
    notifications,
    modals,
    appInfo,
    globalErrors,

    // Getters
    isDarkMode,
    isProduction,
    isDevelopment,
    activeModal,
    hasActiveModal,
    hasGlobalErrors,
    latestGlobalError,

    // Actions
    setLoading,
    startLoading,
    stopLoading,
    updateLoadingProgress,

    // Theme
    setTheme,
    toggleDarkMode,
    applyTheme,
    loadTheme,

    // Sidebar
    toggleSidebar,
    collapseSidebar,
    pinSidebar,

    // Notifications
    addNotification,
    removeNotification,
    clearNotifications,

    // Modals
    openModal,
    closeModal,
    closeAllModals,

    // Global Errors (displayed on page, not redirected)
    addGlobalError,
    dismissGlobalError,
    clearGlobalErrors,
    showGlobalError,

    // Utilities
    getDeviceInfo,
    copyToClipboard,
    downloadFile,
    initializeApp
  }
})